import { omit } from "lodash";
import numbro from "numbro";
import { UnreachableCaseError } from "ts-essentials";

import { Currencies } from "../../constants";
import { type MetricType, MetricTypes } from "../../metrics";

import {
  type MetricFormatter,
  type FormatMetricOptions,
  type MetricPrecision,
  type PrecisionLessFormatMetricOptions,
} from "./types";

const replaceInfinity = (value: string) => value.replace("Infinity", "∞");

/* By default, numbro will just return a string with an "Error:" in it if the value cannot be
   formatted.  We want a hard error to be thrown. */
const throwErrorIfInvalid = <M extends MetricType>(
  value: string,
  config: {
    originalValue: null | number | undefined;
    format: MetricType;
    options: FormatMetricOptions<M>;
  },
) => {
  if (value.includes("ERROR:")) {
    throw new Error(
      `The value '${
        config.originalValue
      }' (type ${typeof config.originalValue}) could not be formatted as '${
        config.format
      }'.\n Options were:\n${JSON.stringify(config.options, null, 2)}`,
    );
  }
  return value;
};

const withFormatter = <M extends MetricType>(
  value: number | null | undefined,
  format: MetricType,

  { infinityLimit = 1e10, ...options }: FormatMetricOptions<M>,
  formatter: (
    v: number,
    o: Omit<FormatMetricOptions<M>, "infinityLimit">,
  ) => string,
) => {
  if (Number.isNaN(value) || value === null || value === undefined) {
    return "—";
  }
  if (infinityLimit && Math.abs(value) > Math.abs(infinityLimit)) {
    value = Infinity * Math.sign(value);
  }
  return throwErrorIfInvalid<M>(replaceInfinity(formatter(value, options)), {
    originalValue: value,
    options: { infinityLimit, ...options } as FormatMetricOptions<M>,
    format,
  });
};

const filterUndefinedOptions = (fmt: numbro.Format) =>
  Object.keys(fmt).reduce(
    (prev: numbro.Format, key: string) =>
      fmt[key as keyof numbro.Format] === undefined
        ? prev
        : { ...prev, [key]: fmt[key as keyof numbro.Format] },
    {},
  );

const PrecisionCurrencyOptionDefaults: {
  [key in MetricPrecision]: (
    v: number,
    providedOptions: numbro.Format,
  ) => numbro.Format;
} = {
  approximate: (v, o) => ({
    average: v >= 1000,
    ...o,
    totalLength: v >= 1000 ? 3 : o.totalLength,
    mantissa: v < 1000 ? (v < 10 ? 2 : 0) : o.mantissa,
    thousandSeparated: true,
  }),
  exact: (v, o) => ({
    thousandSeparated: true,
    mantissa: 2,
    ...o,
  }),
};

type FormatAMetric<M extends MetricType> = {
  (value: number | null | undefined, options: FormatMetricOptions<M>): string;
  approximate: (
    value: number | null | undefined,
    options: PrecisionLessFormatMetricOptions<M>,
  ) => string;
  exact: (
    value: number | null | undefined,
    options: PrecisionLessFormatMetricOptions<M>,
  ) => string;
};

const _formatCurrency: MetricFormatter<"dollars"> = (value, o): string =>
  withFormatter<"dollars">(
    value,
    MetricTypes.CURRENCY,
    o,
    (v, { code, includeSymbol = true, precision = "exact", ...options }) => {
      const opts: numbro.Format = {
        ...PrecisionCurrencyOptionDefaults[precision](v, options),
        output: "currency",
        currencySymbol: includeSymbol ? Currencies.getModel(code).symbol : "",
      };
      return numbro(v.toString()).formatCurrency(filterUndefinedOptions(opts));
    },
  );

export const formatCurrency: FormatAMetric<"dollars"> = Object.assign(
  _formatCurrency,
  {
    approximate: (
      value: number | null | undefined,
      options: PrecisionLessFormatMetricOptions<"dollars">,
    ) =>
      _formatCurrency(value, {
        ...options,
        precision: "approximate",
      } as FormatMetricOptions<"dollars">),
    exact: (
      value: number | null | undefined,
      options: PrecisionLessFormatMetricOptions<"dollars">,
    ) =>
      _formatCurrency(value, {
        ...options,
        precision: "exact",
      } as FormatMetricOptions<"dollars">),
  },
);

const PrecisionDecimalOptionDefaults: {
  [key in MetricPrecision]: (
    v: number,
    providedOptions: numbro.Format,
  ) => numbro.Format;
} = {
  approximate: (v, o) => ({
    average: true,
    totalLength: 3,
    ...o,
  }),
  exact: (v, o) => ({
    thousandSeparated: true,
    mantissa: 2,
    ...o,
  }),
};

const _formatDecimal: MetricFormatter<"decimal"> = (value, o): string =>
  withFormatter<"decimal">(
    value,
    MetricTypes.DECIMAL,
    o,
    (v, { precision = "exact", ...options }) =>
      numbro(v.toString()).format(
        filterUndefinedOptions(
          PrecisionDecimalOptionDefaults[precision](v, options),
        ),
      ),
  );

export const formatDecimal: FormatAMetric<"decimal"> = Object.assign(
  _formatDecimal,
  {
    approximate: (
      value: number | null | undefined,
      options: PrecisionLessFormatMetricOptions<"decimal">,
    ) =>
      _formatDecimal(value, {
        ...options,
        precision: "approximate",
      } as FormatMetricOptions<"decimal">),
    exact: (
      value: number | null | undefined,
      options: PrecisionLessFormatMetricOptions<"decimal">,
    ) =>
      _formatDecimal(value, {
        ...options,
        precision: "exact",
      } as FormatMetricOptions<"decimal">),
  },
);

const PrecisionIntegerOptionDefaults: {
  [key in MetricPrecision]: (
    v: number,
    providedOptions: numbro.Format,
  ) => numbro.Format;
} = {
  approximate: (v, o) => ({
    average: true,
    totalLength: 3,
    ...o,
  }),
  exact: (v, o) => ({
    mantissa: 0,
    thousandSeparated: true,
    ...o,
  }),
};

const _formatInteger: MetricFormatter<"integer"> = (value, o): string =>
  withFormatter<"integer">(
    value,
    MetricTypes.INTEGER,
    o,
    (v, { precision = "exact", ...options }) =>
      numbro(v.toString()).format(
        PrecisionIntegerOptionDefaults[precision](v, options),
      ),
  );

export const formatInteger: FormatAMetric<"integer"> = Object.assign(
  _formatInteger,
  {
    approximate: (
      value: number | null | undefined,
      options: PrecisionLessFormatMetricOptions<"integer">,
    ) =>
      _formatInteger(value, {
        ...options,
        precision: "approximate",
      } as FormatMetricOptions<"integer">),
    exact: (
      value: number | null | undefined,
      options: PrecisionLessFormatMetricOptions<"integer">,
    ) =>
      _formatInteger(value, {
        ...options,
        precision: "exact",
      } as FormatMetricOptions<"integer">),
  },
);

const PrecisionPercentageOptionDefaults: {
  [key in MetricPrecision]: (
    v: number,
    providedOptions: numbro.Format,
  ) => numbro.Format;
} = {
  approximate: (v, o) => ({
    mantissa: 1,
    ...o,
  }),
  exact: (v, o) => ({
    mantissa: 2,
    ...o,
  }),
};

const _formatPercentage: MetricFormatter<"percentage"> = (value, o): string =>
  withFormatter<"percentage">(
    value,
    MetricTypes.PERCENTAGE,
    o,
    (v, { precision = "exact", ...options }) =>
      numbro(v.toString()).format({
        ...PrecisionPercentageOptionDefaults[precision](v, options),
        output: "percent",
      }),
  );

export const formatPercentage: FormatAMetric<"percentage"> = Object.assign(
  _formatPercentage,
  {
    approximate: (
      value: number | null | undefined,
      options: PrecisionLessFormatMetricOptions<"percentage">,
    ) =>
      _formatPercentage(value, {
        ...options,
        precision: "approximate",
      } as FormatMetricOptions<"percentage">),
    exact: (
      value: number | null | undefined,
      options: PrecisionLessFormatMetricOptions<"percentage">,
    ) =>
      _formatPercentage(value, {
        ...options,
        precision: "exact",
      } as FormatMetricOptions<"percentage">),
  },
);

export const formatMultiplier: MetricFormatter<"multiplier"> = (
  value,
  o,
): string =>
  withFormatter<"multiplier">(
    value,
    MetricTypes.MULTIPLIER,
    o,
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    (v, { precision = "exact", ...options }) =>
      numbro(v.toString()).format({
        mantissa: 2,
        ...options,
      }) + " ×",
  );

const _formatMetric = <M extends MetricType>(
  value: number | null | undefined,
  metricType: M,
  options: FormatMetricOptions<M>,
): string => {
  switch (metricType) {
    case MetricTypes.CURRENCY:
      return formatCurrency(value, options as FormatMetricOptions<"dollars">);
    case MetricTypes.DECIMAL:
      return formatDecimal(
        value,
        omit(options, "code") as FormatMetricOptions<"decimal">,
      );
    case MetricTypes.INTEGER:
      return formatInteger(
        value,
        omit(options, "code") as FormatMetricOptions<"integer">,
      );
    case MetricTypes.MULTIPLIER:
      return formatMultiplier(
        value,
        omit(options, "code") as FormatMetricOptions<"multiplier">,
      );
    case MetricTypes.PERCENTAGE:
      return formatPercentage(
        value,
        omit(options, "code") as FormatMetricOptions<"percentage">,
      );
    default:
      throw new UnreachableCaseError(metricType);
  }
};

type FormatMetric = {
  <M extends MetricType>(
    value: number | null | undefined,
    metricType: M,
    options: FormatMetricOptions<M>,
  ): string;
  percentage: FormatAMetric<"percentage">;
  integer: FormatAMetric<"integer">;
  decimal: FormatAMetric<"decimal">;
  currency: FormatAMetric<"dollars">;
  multiplier: typeof formatMultiplier;
  approximate: <M extends MetricType>(
    value: number | null | undefined,
    metricType: M,
    options: PrecisionLessFormatMetricOptions<M>,
  ) => string;
  exact: <M extends MetricType>(
    value: number | null | undefined,
    metricType: M,
    options: PrecisionLessFormatMetricOptions<M>,
  ) => string;
};

/**
 * Formats a number according to the provided metricType format, {@link MetricType}, and options,
 * {@link FormatMetricOptions<M>}.
 *
 * This method is written such that it exposes attributes that allow the optionality of the
 * function to be expressed without the need for explicit options:
 *
 * @example
 * // Performs the formatting with the { precision: "approximate" } option and "dollars" format.
 * formatMetric.currency.approximate(1000, { code: "USD" })
 *
 * @example
 * // Performs the formatting with the { precision: "approximate" } option and "dollars" format.
 * formatMetric.currency(1000, { code: "USD", precision: "approximate" })
 *
 * Note: If the metricType is MetricTypes.CURRENCY, the compiler will not allow you to use the
 * method unless the 'code' option is provided to the method.  Additionally, if the metricType
 * is variable, or any of the allowed formats, the compiler will also not allow you to use the
 * method unless the 'code' option is provided:
 *
 * @example
 * const format: MetricType = ...
 * // The following will not compile:
 * formatMetric(100, format, { precision: "approximate" })
 *
 * @throws {Error}
 *   Throws an error if the numbro library cannot format the value.  Typically, the numbro format
 *   library will return a string with "ERROR:..." in the content - but this method will detect that
 *   and throw instead.
 */
export const formatMetric: FormatMetric = Object.assign(_formatMetric, {
  multiplier: formatMultiplier,
  currency: formatCurrency,
  decimal: formatDecimal,
  integer: formatInteger,
  percentage: formatPercentage,
  approximate: <M extends MetricType>(
    value: number | null | undefined,
    metricType: M,
    options: PrecisionLessFormatMetricOptions<M>,
  ) =>
    _formatMetric(value, metricType, {
      ...options,
      precision: "approximate",
    } as FormatMetricOptions<M>),
  exact: <M extends MetricType>(
    value: number | null | undefined,
    metricType: M,
    options: PrecisionLessFormatMetricOptions<M>,
  ) =>
    _formatMetric(value, metricType, {
      ...options,
      precision: "exact",
    } as FormatMetricOptions<M>),
});
